import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

const GET_MY_TESTS = gql`
  query GetMyTests {
    myTests {
      success
      message
      tests {
        id
        name
        description
        status
        isDefault
        attemptCount
        completedAttemptCount
        passCount
        averageScore
        config {
          questionCount
          timeLimit
          allowRetake
        }
        scoreRanges {
          minScore
          maxScore
          message
          isPassed
        }
        questionSet {
          id
          name
          questionCount
        }
      }
      errors {
        field
        message
      }
    }
  }
`;

const GET_MY_QUESTION_SETS = gql`
  query GetMyQuestionSets {
    myQuestionSets {
      success
      message
      questionSets {
        id
        name
        description
        questionCount
        isDefault
      }
      errors {
        field
        message
      }
    }
  }
`;

const CREATE_TEST = gql`
  mutation CreateTest($input: CreateTestInput!) {
    createTest(input: $input) {
      success
      message
      test {
        id
        name
        description
        status
      }
      errors {
        field
        message
      }
    }
  }
`;

const UPDATE_TEST = gql`
  mutation UpdateTest($input: UpdateTestInput!) {
    updateTest(input: $input) {
      success
      message
      test {
        id
        name
        description
        status
      }
      errors {
        field
        message
      }
    }
  }
`;

const SET_DEFAULT_TEST = gql`
  mutation SetDefaultTest($id: ID!) {
    setDefaultTest(id: $id) {
      success
      message
      test {
        id
        name
        isDefault
      }
      errors {
        field
        message
      }
    }
  }
`;

const DELETE_TEST = gql`
  mutation DeleteTest($id: ID!) {
    deleteTest(id: $id) {
      success
      message
      test {
        id
        name
      }
      errors {
        field
        message
      }
    }
  }
`;

const Tests = () => {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [testForm, setTestForm] = useState({
    name: '',
    description: '',
    questionSetId: '',
    config: {
      questionCount: 0,
      randomize: false,
      timeLimit: 0,
      allowRetake: true,
      retakeInterval: 24
    },
    scoreRanges: [
      { minScore: 0, maxScore: 5, message: '', isPassed: false },
      { minScore: 5, maxScore: 10, message: '', isPassed: false }
    ],
    status: 'DRAFT'
  });

  const { data: testsData, loading: testsLoading } = useQuery(GET_MY_TESTS);
  const { data: questionSetsData, loading: questionSetsLoading } = useQuery(GET_MY_QUESTION_SETS);

  const [createTest, { loading: createLoading }] = useMutation(CREATE_TEST, {
    refetchQueries: [{ query: GET_MY_TESTS }]
  });

  const [updateTest, { loading: updateLoading }] = useMutation(UPDATE_TEST, {
    refetchQueries: [{ query: GET_MY_TESTS }]
  });

  const [setDefaultTest, { loading: setDefaultLoading }] = useMutation(SET_DEFAULT_TEST, {
    refetchQueries: [{ query: GET_MY_TESTS }]
  });

  const [deleteTest, { loading: deleteLoading }] = useMutation(DELETE_TEST, {
    refetchQueries: [{ query: GET_MY_TESTS }]
  });

  const tests = testsData?.myTests?.tests || [];
  const questionSets = questionSetsData?.myQuestionSets?.questionSets || [];
  const defaultQuestionSet = questionSets.find(set => set.isDefault);

  const handleCreateTest = () => {
    console.log('Creating new test');
    setTestForm({
      name: '',
      description: '',
      questionSetId: defaultQuestionSet?.id || '', // 自動使用預設題目集
      config: {
        questionCount: 0,
        randomize: false,
        timeLimit: 0,
        allowRetake: true,
        retakeInterval: 24
      },
      scoreRanges: [
        { minScore: 0, maxScore: 5, message: '不錯！', isPassed: false },
        { minScore: 5, maxScore: 10, message: '很好！', isPassed: false }
      ],
      status: 'DRAFT'
    });
    setShowCreateModal(true);
  };

  const handleEditTest = (test) => {
    console.log('Editing test:', test);
    setEditingTest(test);
    setTestForm({
      name: test.name,
      description: test.description || '',
      questionSetId: test.questionSet.id,
      config: test.config,
      scoreRanges: test.scoreRanges,
      status: test.status
    });
    setShowEditModal(true);
  };

  const handleCopyTestLink = async (testId) => {
    try {
      const testLink = `${window.location.origin}/test/${testId}`;
      await navigator.clipboard.writeText(testLink);
      alert('測試連結已複製到剪貼板！');
    } catch (error) {
      console.error('Failed to copy test link:', error);
      // 降級方案：使用傳統方法
      const textArea = document.createElement('textarea');
      textArea.value = `${window.location.origin}/test/${testId}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('測試連結已複製到剪貼板！');
    }
  };

  const handleSetDefaultTest = async (testId) => {
    console.log('Setting default test:', testId);
    try {
      const result = await setDefaultTest({ variables: { id: testId } });
      if (result.data.setDefaultTest.success) {
        alert('設定預設測試成功！');
      } else {
        alert('設定預設測試失敗：' + result.data.setDefaultTest.message);
      }
    } catch (error) {
      console.error('Error setting default test:', error);
      alert('設定預設測試失敗：' + error.message);
    }
  };

  const handleDeleteTest = async (testId) => {
    console.log('Deleting test:', testId);
    if (!window.confirm('確定要刪除此測試嗎？此操作無法復原。')) {
      return;
    }
    
    try {
      const result = await deleteTest({ variables: { id: testId } });
      if (result.data.deleteTest.success) {
        alert('刪除測試成功！');
      } else {
        alert('刪除測試失敗：' + result.data.deleteTest.message);
      }
    } catch (error) {
      console.error('Error deleting test:', error);
      alert('刪除測試失敗：' + error.message);
    }
  };

  const handleTestFormChange = (field, value) => {
    console.log('Test form field changed:', field, value);
    if (field.startsWith('config.')) {
      const configField = field.split('.')[1];
      setTestForm(prev => ({
        ...prev,
        config: {
          ...prev.config,
          [configField]: value
        }
      }));
    } else if (field.startsWith('scoreRanges.')) {
      const parts = field.split('.');
      const index = parseInt(parts[1]);
      const subField = parts[2];
      setTestForm(prev => ({
        ...prev,
        scoreRanges: prev.scoreRanges.map((range, i) => {
          if (i === index) {
            // 如果是分數欄位，允許空字符串在輸入過程中，但確保最終是數字
            if ((subField === 'minScore' || subField === 'maxScore')) {
              // 如果值是空字符串，保持為空字符串（允許使用者清空輸入）
              if (value === '') {
                return { ...range, [subField]: '' };
              }
              // 否則轉換為數字
              const numValue = typeof value === 'number' ? value : parseFloat(value);
              return { ...range, [subField]: isNaN(numValue) ? 0 : numValue };
            }
            return { ...range, [subField]: value };
          }
          return range;
        })
      }));
    } else {
      setTestForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const addScoreRange = () => {
    setTestForm(prev => ({
      ...prev,
      scoreRanges: [...prev.scoreRanges, { minScore: 0, maxScore: 5, message: '', isPassed: false }]
    }));
  };

  const removeScoreRange = (index) => {
    setTestForm(prev => ({
      ...prev,
      scoreRanges: prev.scoreRanges.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitTest = async () => {
    console.log('Submitting test form:', testForm);
    
    if (!testForm.name.trim()) {
      alert('測試名稱不能為空');
      return;
    }

    // 創建測試時自動使用預設題目集
    const finalQuestionSetId = editingTest 
      ? testForm.questionSetId 
      : (defaultQuestionSet?.id || testForm.questionSetId);

    if (!finalQuestionSetId) {
      alert('找不到預設題目集，請先創建題目集');
      return;
    }

    // 確保分數區間的分數都是數字類型
    const cleanedScoreRanges = testForm.scoreRanges.map(range => ({
      ...range,
      minScore: typeof range.minScore === 'number' ? range.minScore : (parseFloat(range.minScore) || 0),
      maxScore: typeof range.maxScore === 'number' ? range.maxScore : (parseFloat(range.maxScore) || 0)
    }));

    try {
      if (editingTest) {
        // 過濾掉不需要的欄位
        const { questionSetId, ...updateData } = testForm;
        
        // 過濾掉 config 和 scoreRanges 中的 __typename
        if (updateData.config) {
          const { __typename, ...config } = updateData.config;
          updateData.config = config;
        }
        
        if (updateData.scoreRanges) {
          updateData.scoreRanges = cleanedScoreRanges.map(range => {
            const { __typename, ...cleanRange } = range;
            return cleanRange;
          });
        }
        
        const result = await updateTest({
          variables: {
            input: {
              id: editingTest.id,
              ...updateData
            }
          }
        });

        if (result.data.updateTest.success) {
          console.log('Test updated successfully');
          setShowEditModal(false);
          setEditingTest(null);
        } else {
          console.log('Update test failed:', result.data.updateTest.message);
          alert('更新測試失敗：' + result.data.updateTest.message);
        }
      } else {
        const result = await createTest({
          variables: {
            input: {
              ...testForm,
              questionSetId: finalQuestionSetId, // 使用預設題目集
              scoreRanges: cleanedScoreRanges // 使用清理後的分數區間
            }
          }
        });

        if (result.data.createTest.success) {
          console.log('Test created successfully');
          setShowCreateModal(false);
        } else {
          console.log('Create test failed:', result.data.createTest.message);
          alert('創建測試失敗：' + result.data.createTest.message);
        }
      }
    } catch (error) {
      console.error('Test operation error:', error);
      alert('操作失敗，請稍後再試');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      DRAFT: { text: '草稿', color: 'bg-gray-100 text-gray-800' },
      ACTIVE: { text: '啟用', color: 'bg-green-100 text-green-800' },
      INACTIVE: { text: '停用', color: 'bg-red-100 text-red-800' }
    };
    const statusInfo = statusMap[status] || statusMap.DRAFT;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  if (testsLoading || questionSetsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-8 gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">測試管理</h1>
            <p className="text-gray-600 text-sm sm:text-base">
              你可以決定不同分數區間的人可以看見的內容，可以是暗號，可以是照片連結，甚至聯繫方式。可以分享連結給沒有使用這個軟體的人
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={() => navigate('/voting?tab=questions')}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-all shadow-sm hover:shadow-md w-full sm:w-auto"
            >
              返回擇友標準
            </button>
            <button
              onClick={handleCreateTest}
              className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-md hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-md hover:shadow-lg w-full sm:w-auto"
            >
              創建測試
            </button>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {tests.map((test) => (
              <li key={test.id} className="px-4 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <h3 className="text-lg font-medium text-gray-900">{test.name}</h3>
                      {getStatusBadge(test.status)}
                      {test.isDefault && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          預設
                        </span>
                      )}
                    </div>
                    {test.description && (
                      <p className="mt-1 text-sm text-gray-500">{test.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-500">
                      <span>題目集：{test.questionSet.name}</span>
                      <span>題目數：{test.questionSet.questionCount}</span>
                      {/* 嘗試次數和通過次數已移除，不需要顯示 */}
                      {test.averageScore > 0 && (
                        <span>平均分：{test.averageScore.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:space-x-2">
                    <button
                      onClick={() => handleCopyTestLink(test.id)}
                      className="px-3 py-1.5 bg-purple-400 text-white text-sm rounded-md hover:bg-purple-500 transition-colors flex-1 sm:flex-none"
                    >
                      複製測試連結
                    </button>
                    {!test.isDefault && (
                      <button
                        onClick={() => handleSetDefaultTest(test.id)}
                        className="px-3 py-1.5 bg-purple-400 text-white text-sm rounded-md hover:bg-purple-500 transition-colors flex-1 sm:flex-none"
                        disabled={setDefaultLoading}
                      >
                        設為預設
                      </button>
                    )}
                    <button
                      onClick={() => handleEditTest(test)}
                      className="px-3 py-1.5 bg-yellow-400 text-white text-sm rounded-md hover:bg-yellow-500 transition-colors flex-1 sm:flex-none"
                    >
                      編輯
                    </button>
                    {!test.isDefault && (
                      <button
                        onClick={() => handleDeleteTest(test.id)}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 flex-1 sm:flex-none"
                        disabled={deleteLoading}
                      >
                        刪除
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          
          {tests.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">還沒有創建任何測試</p>
              <button
                onClick={handleCreateTest}
                className="mt-4 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-md hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-md hover:shadow-lg"
              >
                創建第一個測試
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 創建測試彈窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">創建測試</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    測試名稱
                  </label>
                  <input
                    type="text"
                    value={testForm.name}
                    onChange={(e) => handleTestFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="輸入測試名稱"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述
                  </label>
                  <textarea
                    value={testForm.description}
                    onChange={(e) => handleTestFormChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="輸入測試描述"
                  />
                </div>

                {/* 題目集已自動使用預設題目集，不需要選擇 */}
                {defaultQuestionSet && (
                  <div className="bg-gray-50 px-3 py-2 rounded-md">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">使用擇友標準作為題庫</span>
                      <span className="ml-2 text-gray-500">({defaultQuestionSet.questionCount} 題)</span>
                    </p>
                  </div>
                )}

                {/* 配置項已固定為默認值，不需要顯示 */}
                {/* 抽題數量: 0 (全部) */}
                {/* 時間限制: 0 (無限制) */}
                {/* 隨機抽題: off */}
                {/* 允許重複作答: on */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    設定分數區間與顯示的文字
                  </label>
                  <div className="space-y-2">
                    {testForm.scoreRanges.map((range, index) => (
                      <div key={index} className="space-y-2 p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={range.minScore === '' || range.minScore === null || range.minScore === undefined ? '' : range.minScore}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // 允許空字符串，讓使用者可以清空輸入
                              if (inputValue === '') {
                                handleTestFormChange(`scoreRanges.${index}.minScore`, '');
                                return;
                              }
                              // 轉換為數字
                              const numValue = parseFloat(inputValue);
                              if (!isNaN(numValue)) {
                                handleTestFormChange(`scoreRanges.${index}.minScore`, numValue);
                              }
                            }}
                            onBlur={(e) => {
                              // 失去焦點時，如果為空或無效，設為0
                              const inputValue = e.target.value;
                              if (inputValue === '' || isNaN(parseFloat(inputValue))) {
                                handleTestFormChange(`scoreRanges.${index}.minScore`, 0);
                              }
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="最小分"
                          />
                          <span className="text-sm text-gray-500">到</span>
                          <input
                            type="number"
                            value={range.maxScore === '' || range.maxScore === null || range.maxScore === undefined ? '' : range.maxScore}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // 允許空字符串，讓使用者可以清空輸入
                              if (inputValue === '') {
                                handleTestFormChange(`scoreRanges.${index}.maxScore`, '');
                                return;
                              }
                              // 轉換為數字
                              const numValue = parseFloat(inputValue);
                              if (!isNaN(numValue)) {
                                handleTestFormChange(`scoreRanges.${index}.maxScore`, numValue);
                              }
                            }}
                            onBlur={(e) => {
                              // 失去焦點時，如果為空或無效，設為0
                              const inputValue = e.target.value;
                              if (inputValue === '' || isNaN(parseFloat(inputValue))) {
                                handleTestFormChange(`scoreRanges.${index}.maxScore`, 0);
                              }
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="最大分"
                          />
                          {testForm.scoreRanges.length > 1 && (
                            <button
                              onClick={() => removeScoreRange(index)}
                              className="ml-auto text-red-600 hover:text-red-800 text-sm"
                            >
                              刪除
                            </button>
                          )}
                        </div>
                        <textarea
                          value={range.message}
                          onChange={(e) => handleTestFormChange(`scoreRanges.${index}.message`, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="顯示訊息"
                          rows={3}
                        />
                        {/* 通過與否已移除，不需要設定 */}
                      </div>
                    ))}
                    <button
                      onClick={addScoreRange}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      + 新增分數區間
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  disabled={createLoading}
                >
                  取消
                </button>
                <button
                  onClick={handleSubmitTest}
                  className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-md hover:from-yellow-500 hover:to-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                  disabled={createLoading}
                >
                  {createLoading ? '創建中...' : '創建測試'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 編輯測試彈窗 */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">編輯測試</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    測試名稱
                  </label>
                  <input
                    type="text"
                    value={testForm.name}
                    onChange={(e) => handleTestFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="輸入測試名稱"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述
                  </label>
                  <textarea
                    value={testForm.description}
                    onChange={(e) => handleTestFormChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="輸入測試描述"
                  />
                </div>

                {/* 題目集資訊（編輯時顯示，不可修改） */}
                {editingTest && editingTest.questionSet && (
                  <div className="bg-gray-50 px-3 py-2 rounded-md">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">使用擇友標準作為題庫</span>
                      <span className="ml-2 text-gray-500">({editingTest.questionSet.questionCount} 題)</span>
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    狀態
                  </label>
                  <select
                    value={testForm.status}
                    onChange={(e) => handleTestFormChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="DRAFT">草稿</option>
                    <option value="ACTIVE">啟用</option>
                    <option value="INACTIVE">停用</option>
                  </select>
                </div>

                {/* 配置項已固定為默認值，不需要顯示 */}
                {/* 抽題數量: 0 (全部) */}
                {/* 時間限制: 0 (無限制) */}
                {/* 隨機抽題: off */}
                {/* 允許重複作答: on */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    設定分數區間與顯示的文字
                  </label>
                  <div className="space-y-2">
                    {testForm.scoreRanges.map((range, index) => (
                      <div key={index} className="space-y-2 p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={range.minScore === '' || range.minScore === null || range.minScore === undefined ? '' : range.minScore}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // 允許空字符串，讓使用者可以清空輸入
                              if (inputValue === '') {
                                handleTestFormChange(`scoreRanges.${index}.minScore`, '');
                                return;
                              }
                              // 轉換為數字
                              const numValue = parseFloat(inputValue);
                              if (!isNaN(numValue)) {
                                handleTestFormChange(`scoreRanges.${index}.minScore`, numValue);
                              }
                            }}
                            onBlur={(e) => {
                              // 失去焦點時，如果為空或無效，設為0
                              const inputValue = e.target.value;
                              if (inputValue === '' || isNaN(parseFloat(inputValue))) {
                                handleTestFormChange(`scoreRanges.${index}.minScore`, 0);
                              }
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="最小分"
                          />
                          <span className="text-sm text-gray-500">到</span>
                          <input
                            type="number"
                            value={range.maxScore === '' || range.maxScore === null || range.maxScore === undefined ? '' : range.maxScore}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              // 允許空字符串，讓使用者可以清空輸入
                              if (inputValue === '') {
                                handleTestFormChange(`scoreRanges.${index}.maxScore`, '');
                                return;
                              }
                              // 轉換為數字
                              const numValue = parseFloat(inputValue);
                              if (!isNaN(numValue)) {
                                handleTestFormChange(`scoreRanges.${index}.maxScore`, numValue);
                              }
                            }}
                            onBlur={(e) => {
                              // 失去焦點時，如果為空或無效，設為0
                              const inputValue = e.target.value;
                              if (inputValue === '' || isNaN(parseFloat(inputValue))) {
                                handleTestFormChange(`scoreRanges.${index}.maxScore`, 0);
                              }
                            }}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="最大分"
                          />
                          {testForm.scoreRanges.length > 1 && (
                            <button
                              onClick={() => removeScoreRange(index)}
                              className="ml-auto text-red-600 hover:text-red-800 text-sm"
                            >
                              刪除
                            </button>
                          )}
                        </div>
                        <textarea
                          value={range.message}
                          onChange={(e) => handleTestFormChange(`scoreRanges.${index}.message`, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                          placeholder="顯示訊息"
                          rows={3}
                        />
                        {/* 通過與否已移除，不需要設定 */}
                      </div>
                    ))}
                    <button
                      onClick={addScoreRange}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      + 新增分數區間
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingTest(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  disabled={updateLoading}
                >
                  取消
                </button>
                <button
                  onClick={handleSubmitTest}
                  className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white rounded-md hover:from-yellow-500 hover:to-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                  disabled={updateLoading}
                >
                  {updateLoading ? '更新中...' : '更新測試'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tests; 